import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Camera, LayoutDashboard, Images, Upload, Settings, LogOut, X, ChevronRight } from 'lucide-react';
import axios from 'axios';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: '/dashboard/photos', icon: Images, label: 'Photos' },
  { to: '/dashboard/upload', icon: Upload, label: 'Upload' },
  { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
];

const Sidebar = ({ user, setUser, open, onClose }) => {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await axios.post('/api/auth/logout'); } catch {}
    setUser(null);
    navigate('/login');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-card/95 backdrop-blur-xl border-r border-border/50 z-50 flex flex-col transform transition-all duration-300 ease-out lg:sticky lg:top-0 lg:h-screen lg:z-auto ${
          open ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0 lg:w-0 lg:opacity-0 lg:border-r-0 overflow-hidden'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-border/50 shrink-0 w-64">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-primary rounded-2xl flex items-center justify-center shadow-bubble">
              <Camera size={16} strokeWidth={2.5} className="text-primary-foreground" />
            </div>
            <div>
              <span className="font-heading font-bold text-sm text-foreground tracking-tight">Photo Album</span>
              <span className="block text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">Dashboard</span>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin w-64" aria-label="Dashboard navigation">
          <p className="px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Menu</p>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-bold transition-all duration-200 ${
                  isActive
                    ? 'bg-primary/8 text-primary border border-primary/15'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent'
                }`
              }
              onClick={() => {
                if (window.innerWidth < 1024) {
                  onClose();
                }
              }}
            >
              {({ isActive }) => (
                <>
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isActive ? 'bg-primary/15' : 'bg-muted group-hover:bg-muted/80'
                  }`}>
                    <item.icon size={16} className={isActive ? 'text-primary' : ''} />
                  </div>
                  <span className="flex-1">{item.label}</span>
                  {isActive && <ChevronRight size={14} className="text-primary/40" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-border/50 shrink-0 w-64">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-muted/60">
            <div className="w-8 h-8 bg-primary/15 rounded-full flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-primary uppercase">
                {user?.username?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground truncate">{user?.username}</p>
              <p className="text-[11px] text-muted-foreground font-medium truncate">Admin</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-colors cursor-pointer"
              aria-label="Logout"
              title="Logout"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
